import { memo, useCallback, useEffect, useMemo, useRef } from "react"
import { FlatList, Image, ListRenderItemInfo, Platform, ScrollView, StyleSheet, Text, View } from "react-native"
import { Button } from "src/components/parts/buttons/Button"
import { DoubleButtonFooter } from "src/components/parts/DoubleButtonFooter"
import { CreateOrganizationIcon } from "src/components/parts/icons/CreateOrganizationIcon"
import { JoinIcon } from "src/components/parts/icons/JoinIcon"
import { JoinOrCreateOrganizationActionView } from "src/components/parts/JoinOrCreateOrganizationActionView"
import { CustomRefreshControl } from "src/components/projects/CustomRefreshControl"
import { Colors } from "src/constants/Colors"
import { useAsyncSelector } from "src/hooks/useAsyncSelector"
import { useRefresher } from "src/hooks/useRefresher"
import { MyOrganization } from "src/queries/getMyOrganizations"
import { myFullOrganizationsRequestIdState } from "src/recoil/atoms/organization/myOrganizationsRequestIdState"
import { myFullOrganizationsSelector } from "src/recoil/selectors/organization/myOrganizationsSelector"
import { OrganizationManagerStackScreenProps } from "src/types.d"
import { CustomAlert } from "src/utils/CustomAlert"
import { OrganizationManagerScreens } from "src/constants/OrganizationManagerScreens"
import { Screens } from "src/constants/Screens"
import { RootTabs } from "src/constants/RootTabs"
import { BadgeIcon } from "src/components/parts/icons/BadgeIcon"
import { hasPreTeamMemberSelector } from "src/recoil/selectors/bottomTab/hasPreTeamMemberSelector"
import { MaxCount } from "src/constants/MaxCount"
import { Analytics } from "src/tags/analytics/Analytics"
import { adLoadState, isLoadDoneAd } from "src/recoil/atoms/ads/adState"
import { useRecoilState, useSetRecoilState } from "recoil"
import { notificationsRequestIdState } from "src/recoil/atoms/home/notificationsRequestIdState"
import { defaultTeam } from "src/constants/defaultTeam"
import { MainLayout } from "src/layouts/MainLayout"
import { selectedMyOrganizationState } from "src/recoil/atoms/organization/selectedMyOrganizationState"
import { useAsyncState } from "src/hooks/useAsyncState"
import { listAllMigratedTeamsRequestIdState } from "src/recoil/atoms/organization/listAllMigratedTeamsRequestIdState"
import { notificationsSelector } from "src/recoil/selectors/home/notificationsSelector"
import { myListTeamRequestIdState } from "src/recoil/atoms/team/myListTeamRequestIdState"
import { organizationMemberImageRefresherState } from "src/recoil/atoms/organization/organizationMemberImageRefresherState"
import { useIsFocused } from "@react-navigation/native"
import { TeamNoticeType } from "src/aws/customAPI"
import { useAdTargetingData } from "src/hooks/useAdTargetingData"
import { useFetchBanner } from "src/hooks/useFetchBanner"
import { usePseudoWindowWidthForOrganizationIcon } from "src/hooks/useCheckPCScreen"

export const OrganizationManagerTab = memo<
  OrganizationManagerStackScreenProps<typeof OrganizationManagerScreens.OrganizationManager>
>(({ navigation }) => {
  const refreshAllMigratedTeams = useRefresher(listAllMigratedTeamsRequestIdState)
  const { value: organizations, isLoading: isMyOrganizationsLoading } = useAsyncSelector(myFullOrganizationsSelector)
  const { value: notifications } = useAsyncSelector(notificationsSelector)
  const [selectedOrganizationId] = useAsyncState(selectedMyOrganizationState)
  const setRefreshImage = useSetRecoilState(organizationMemberImageRefresherState)
  const refreshMyOrganizations = useRefresher(myFullOrganizationsRequestIdState)
  const refreshNotifications = useRefresher(notificationsRequestIdState)
  const refreshListTeamGql = useRefresher(myListTeamRequestIdState)
  const hasTriedAutoRefresh = useRef(false)
  const isShowTeamListAd = useMemo(() => {
    if (organizations != null) {
      return organizations.some((item) => !item.paidFunctionEnabled || item.showAdsFlg)
    }
    return true
  }, [organizations])
  const refresh = useCallback(() => {
    refreshListTeamGql()
    refreshMyOrganizations()
    refreshNotifications()
    refreshAllMigratedTeams()
  }, [refreshMyOrganizations, refreshNotifications, refreshAllMigratedTeams, refreshListTeamGql])
  const isFocused = useIsFocused()
  const iconWidth = usePseudoWindowWidthForOrganizationIcon()
  const imageWidth = useMemo(() => iconWidth - 28, [iconWidth])
  const imageHeight = useMemo(() => imageWidth * 0.54, [imageWidth])
  const { value: hasPreTeamMemberByTeam } = useAsyncSelector(hasPreTeamMemberSelector)
  const [adLoadStateList] = useRecoilState(adLoadState)
  const showBadge = useCallback(
    (organizationId: string) => {
      const paidFunctionNotice =
        notifications
          ?.filter(
            (notice) =>
              notice.organizationId === organizationId &&
              (notice.type === TeamNoticeType.paymentDeadline ||
                notice.type === TeamNoticeType.transferApplicant ||
                notice.type === TeamNoticeType.startExtendOrderTime ||
                notice.type === TeamNoticeType.middleExtendOrderTime)
          )
          .some((hasBadge) => !hasBadge.isRead) ?? false
      return (hasPreTeamMemberByTeam || {})[organizationId] || paidFunctionNotice
    },
    [hasPreTeamMemberByTeam, notifications]
  )
  const gotoOrganizationDetail = useCallback(
    (id: string) => {
      if (selectedOrganizationId !== id) {
        setRefreshImage(true)
      }
      navigation.navigate(Screens.Root, {
        screen: RootTabs.OrganizationManagerTab,
        params: {
          screen: OrganizationManagerScreens.OrganizationDetail,
          params: { organizationId: id },
        },
      })
    },
    [navigation, setRefreshImage, selectedOrganizationId]
  )

  const { data: adTargetingData } = useAdTargetingData()
  const homeBottomBanner3 = useFetchBanner("HomeBottom_3", adTargetingData)
  const RenderItem = useCallback(
    ({ item: { id, title, uriImage }, index }: ListRenderItemInfo<MyOrganization>) => {
      const handlePress = () => {
        gotoOrganizationDetail(id)
      }
      return (
        <View style={styles.webCategory}>
          <ListItem
            id={id}
            title={title}
            uriImage={uriImage ?? undefined}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            maxImageWidth={imageWidth}
            handlePress={handlePress}
            showBadge={showBadge(id)}
          />
          {Platform.OS !== "web" && isShowTeamListAd && organizations && index === organizations.length - 1 ? (
            <View style={!isLoadDoneAd("home_bottom", adLoadStateList, 3) && { height: 0 }}>{homeBottomBanner3}</View>
          ) : null}
        </View>
      )
    },
    [
      imageWidth,
      imageHeight,
      showBadge,
      organizations,
      adLoadStateList,
      gotoOrganizationDetail,
      isShowTeamListAd,
      homeBottomBanner3,
    ]
  )

  const gotoJoinOrganization = useCallback(async () => {
    await Analytics.sendJoinRequest()
    if ((organizations?.length || 0) >= MaxCount.Team) {
      CustomAlert.alert(
        "エラー",
        `所属団体数が上限を超えるため、参加できません。\n※1人の方が所属できる団体数は最大${MaxCount.Team}団体です。`
      )
      return
    }
    navigation.navigate("JoinOrganization")
  }, [navigation, organizations])

  const gotoCreateOrganization = useCallback(() => {
    if ((organizations?.length || 0) >= MaxCount.Team) {
      CustomAlert.alert(
        "エラー",
        `所属団体数が上限を超えるため、作成できません。\n※1人の方が所属できる団体数は最大${MaxCount.Team}団体です。`
      )
      return
    }
    navigation.navigate("CreateOrganizationStack")
  }, [navigation, organizations])

  // 初回表示時、データ取得に失敗した可能性がある場合は自動リトライ
  useEffect(() => {
    if (!hasTriedAutoRefresh.current && !isMyOrganizationsLoading) {
      // データがnullまたは空配列の場合、1秒後に自動リフレッシュ
      if (organizations == null || (organizations.length === 0 && isFocused)) {
        hasTriedAutoRefresh.current = true
        const timer = setTimeout(() => {
          refresh()
        }, 1000)
        return () => clearTimeout(timer)
      }
    }
  }, [organizations, isMyOrganizationsLoading, isFocused, refresh])

  // ローディング中は明示的にローディング状態を表示
  if (organizations == null && isMyOrganizationsLoading) {
    return (
      <MainLayout>
        <View style={styles.container} />
      </MainLayout>
    )
  }

  // データ取得完了後、所属団体がない場合
  if (organizations != null && organizations.length === 0) {
    return (
      <MainLayout>
        <ScrollView
          contentContainerStyle={styles.container}
          refreshControl={<CustomRefreshControl refreshing={isMyOrganizationsLoading} onRefresh={refresh} />}
        >
          {
            <JoinOrCreateOrganizationActionView
              join={gotoJoinOrganization}
              create={gotoCreateOrganization}
              refresh={refresh}
              isLoadingRefesh={isMyOrganizationsLoading}
            />
          }
        </ScrollView>
      </MainLayout>
    )
  }

  // organizations == null だが isLoading = false の場合（エラーの可能性）
  if (organizations == null) {
    return (
      <MainLayout>
        <ScrollView
          contentContainerStyle={styles.container}
          refreshControl={<CustomRefreshControl refreshing={isMyOrganizationsLoading} onRefresh={refresh} />}
        >
          {
            <JoinOrCreateOrganizationActionView
              join={gotoJoinOrganization}
              create={gotoCreateOrganization}
              refresh={refresh}
              isLoadingRefesh={isMyOrganizationsLoading}
            />
          }
        </ScrollView>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <View style={styles.container}>
        <View style={styles.main}>
          <FlatList
            data={organizations}
            numColumns={1}
            renderItem={RenderItem}
            keyExtractor={(item, index) => item.id + index.toString()}
            refreshControl={<CustomRefreshControl refreshing={isMyOrganizationsLoading && isFocused} onRefresh={refresh} />}
          />
        </View>
        {
          <DoubleButtonFooter
            leftButtonTitle="団体に参加"
            rightButtonTitle="団体を作成"
            LeftButtonIcon={<JoinIcon size={24} />}
            RightButtonIcon={<CreateOrganizationIcon size={33} />}
            onLeftButtonPress={gotoJoinOrganization}
            onRightButtonPress={gotoCreateOrganization}
          />
        }
      </View>
    </MainLayout>
  )
})

type ListItemProps = {
  id: string
  title: string
  uriImage?: string
  imageWidth: number
  imageHeight: number
  maxImageWidth: number
  handlePress: () => void
  showBadge: boolean
}

const ListItem = memo<ListItemProps>(({ title, uriImage, handlePress, imageWidth, imageHeight, maxImageWidth, showBadge }) => {
  return (
    <View style={styles.itemContainer}>
      <View style={{ maxWidth: maxImageWidth }}>
        <Button onPress={handlePress} style={styles.imageButton}>
          <Image
            source={uriImage ? { uri: uriImage } : defaultTeam}
            style={[styles.itemImage, { width: imageWidth, height: imageHeight, maxWidth: maxImageWidth }]}
            resizeMode="cover"
          />
        </Button>
        <View style={[styles.itemLabel]}>
          {showBadge ? <BadgeIcon size={"small"} /> : null}
          <Button onPress={handlePress}>
            <Text style={[styles.label]}>{title}</Text>
          </Button>
        </View>
      </View>
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  main: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  itemContainer: {
    paddingVertical: 6,
    alignItems: "center",
  },
  itemImage: {
    borderRadius: 8,
  },
  itemLabel: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  label: {
    fontSize: 14,
    fontWeight: "bold",
    padding: 12,
    textAlign: "center",
  },
  imageButton: {
    shadowColor: "#e0d6ca",
    shadowOffset: { width: 0, height: 0 },
    shadowRadius: 17,
    elevation: 3,
    borderRadius: 8,
  },
  migrateContainer: {
    paddingHorizontal: 3,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.white2,
    backgroundColor: Colors.white3,
  },
  migrateButtonContainer: {
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 17,
    backgroundColor: Colors.orange,
    borderRadius: 10,
    width: "100%",
    height: "100%",
  },
  listTeamMigrateButtonContainer: {
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 17,
    backgroundColor: Colors.leaf,
    borderRadius: 10,
    width: "100%",
    height: "100%",
  },
  migrateButtonText: {
    color: Colors.white3,
    fontSize: 15,
    fontWeight: "700",
    lineHeight: 20,
    textAlign: "center",
  },
  aboutMigrationContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 16,
  },
  aboutMigrationText: {
    color: Colors.greyshBrown,
    textDecorationLine: "underline",
    marginLeft: 8,
  },
  popUpNoticeContainer: {
    flexDirection: "row",
    marginTop: 24,
    paddingHorizontal: 15,
  },
  popUpNoticeText: {
    paddingHorizontal: 10,
    paddingTop: 10,
  },
  modalWrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.37)",
    padding: 22,
  },
  webCategories: {
    paddingVertical: 24,
  },
  webCategory: {
    flex: 1,
    padding: 6,
    paddingTop: 16,
    paddingBottom: 0,
  },
  itemWrapper: {
    marginTop: 40,
    alignItems: "center",
  },
  cardCategory: {
    alignItems: "center",
    paddingTop: 10,
    paddingBottom: 15,
    paddingHorizontal: 10,
    display: "flex",
    flexDirection: "column",
    backgroundColor: Colors.white3,
    borderRadius: 8,
    shadowColor: "#e0d6ca",
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 0.5,
    shadowRadius: 17,
    elevation: 5,
  },
  doubleButtonHeader: {
    flexDirection: "row",
    width: "100%",
  },
  buttonContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 10,
    flexDirection: "row",
    width: "43%",
  },
  centerRow: {
    alignItems: "center",
  },
  headerBar: {
    height: 40,
    backgroundColor: Colors.red,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  contentLink: {
    fontSize: 14,
    fontWeight: "400",
    textAlign: "left",
  },
  linkText: {
    color: Colors.orange,
    textDecorationLine: "underline",
  },
})
